import { ModalType } from '../../../../app/package/base/service/modals/modal-service';
import type { GameDetails } from '../../../base/package/api/package/gaming/gaming-domain';
import { convertTimestampToFullDate, convertTimestampToHourMinute } from '../../../base/package/util/time';
import { type Bonus, BonusStage, BonusState } from '../../../mod-bonus/types';
import { getGame } from '../../../mod-bonus/utils/bonus-utils';
import { UILogoutModal } from '../logout-modal/ui-logout-modal';
import { UIFreeSpinLogoutModal } from './ui-free-spin-logout-modal';

/**
 * Checks for active free spins before showing the logout modal.
 * If free spins are available, shows the free-spin-logout modal with game tiles.
 * Otherwise, falls through to the standard logout confirmation modal.
 */
export function showLogoutModal(): void {
  // Feature gated via casino config (BackOffice) — defaults to disabled
  if (!window.$app.casinoConfig.freespinsLogoutModalEnabled) {
    _showStandardLogout();
    return;
  }

  // SH Casino does not support the free-spin logout warning
  if (window.$app.appSettings.isSHCasino) {
    _showStandardLogout();
    return;
  }

  // Refresh bonus data to catch newly awarded free spins (e.g. mid-session from gameplay)
  const freshData = window.$app.bonus.getActualBonuses().then(() => window.$app.bonus.bonusStore.value);
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000));

  Promise.race([freshData, timeout]).then((result) => {
    const bonusData = result ?? window.$app.bonus.bonusStore.value;
    _evaluateAndShow(bonusData.actualBonuses);
  });
}

function _evaluateAndShow(actualBonuses: Bonus[] | null): void {
  if (!actualBonuses) {
    _showStandardLogout();
    return;
  }

  const freeSpinBonuses = actualBonuses.filter(
    (b) => b.state === BonusState.ACTIVE && b.stage === BonusStage.FREE_SPIN_ACTIVE,
  );

  if (freeSpinBonuses.length === 0) {
    _showStandardLogout();
    return;
  }

  // Gate: skip the modal if no spins remain at all
  const hasAnySpins = freeSpinBonuses.some((b) => (b.bonusBalance.freeSpin?.count ?? 0) > 0);
  if (!hasAnySpins) {
    _showStandardLogout();
    return;
  }

  // For single bonus, use its bonusId for direct reward navigation
  const bonusId = freeSpinBonuses.length === 1 ? freeSpinBonuses[0]!.bonusId : '';

  // Timeout guard: if campaign/game resolution takes too long, fall through to standard logout.
  // The flag prevents a late-resolving _showFreeSpinLogout from showing a second modal.
  let timedOut = false;
  const logoutTimeout = new Promise<'timeout'>((resolve) =>
    setTimeout(() => {
      timedOut = true;
      resolve('timeout');
    }, 3000),
  );
  Promise.race([_showFreeSpinLogout(freeSpinBonuses, bonusId, () => timedOut), logoutTimeout]).then((result) => {
    if (result === 'timeout') {
      _showStandardLogout();
    }
  });
}

function _showStandardLogout(): void {
  const modal = new UILogoutModal();
  window.$app.modal.show(modal, ModalType.LoggedOut);
}

async function _showFreeSpinLogout(
  freeSpinBonuses: Bonus[],
  bonusId: string,
  isCancelled: () => boolean,
): Promise<void> {
  const campaignIds = freeSpinBonuses.map((b) => b.campaignId);

  const campaigns = await window.$app.bonus.getCampaignInfoByIds(campaignIds).catch((err) => {
    window.$app.logger.warn('Failed to load free spin campaigns for logout modal', err);
    return null;
  });

  // Compute the displayed count from campaign numFreeRounds (awarded per bonus),
  // matching what the gaming hub reward tiles show.
  let totalFreeSpinCount = 0;
  const allGames: GameDetails[] = [];

  if (campaigns?.length) {
    // Build a lookup: campaignId → numFreeRounds
    const numFreeRoundsMap = new Map<string, number>();
    for (const campaign of campaigns) {
      const rounds = campaign?.externalConfig?.providerCampaign?.numFreeRounds ?? 0;
      numFreeRoundsMap.set(campaign.id, rounds);
    }

    // Each bonus activation contributes its campaign's numFreeRounds
    for (const bonus of freeSpinBonuses) {
      totalFreeSpinCount += numFreeRoundsMap.get(bonus.campaignId) ?? 0;
    }

    for (const campaign of campaigns) {
      const providerCampaign = campaign?.externalConfig?.providerCampaign;
      if (!providerCampaign?.games?.length) continue;

      const results = await Promise.all(
        providerCampaign.games.map((gameId) => getGame(gameId, providerCampaign.integration)),
      );
      for (const game of results) {
        if (game && !allGames.some((g) => g.id === game.id)) {
          allGames.push(game);
        }
        // Deck only renders 3 tiles max
        if (allGames.length >= 3) break;
      }
      if (allGames.length >= 3) break;
    }
  }

  // Fallback: if campaign fetch failed, sum from bonus balance
  if (totalFreeSpinCount === 0) {
    totalFreeSpinCount = freeSpinBonuses.reduce((sum, b) => sum + (b.bonusBalance.freeSpin?.count ?? 0), 0);
  }

  if (totalFreeSpinCount === 0) {
    if (!isCancelled()) _showStandardLogout();
    return;
  }

  if (isCancelled()) return;

  // Use the earliest expiry date across all active free spin bonuses
  const earliestExpiry = freeSpinBonuses
    .map((b) => b.expires)
    .filter(Boolean)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

  const locale = window.$app.appSettings.localeFormat;
  const modal = new UIFreeSpinLogoutModal();
  modal.games = allGames;
  modal.freeSpinCount = totalFreeSpinCount;
  modal.bonusId = bonusId;
  modal.expiresAt = earliestExpiry
    ? `${convertTimestampToFullDate(earliestExpiry, locale)}, ${convertTimestampToHourMinute(earliestExpiry, locale)}`
    : '';
  window.$app.modal.show(modal, ModalType.FreeSpinLogout);
}
